import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { reviewPlatformKeys } from '@/lib/queryKeys';
import type {
  AvailableBusinessesResponse,
  OAuthAuthorizationQuery,
  OAuthAuthorizationUrlResponse,
} from '@/types/review-platform';

interface OAuthUrlVariables extends OAuthAuthorizationQuery {
  platformSlug: 'google' | 'facebook';
}

/**
 * Fetch the OAuth authorization URL for the given platform. The caller then
 * redirects the browser (popup or full-page) to `authorizationUrl`. The
 * provider redirects the user back to the operations service callback, which
 * then redirects the browser to:
 *
 *   {FRONTEND_URL}/dashboard/settings/review-platforms?provider={slug}
 *     &status=select-business&oauthTokenId=<id>
 *
 * The page reads `oauthTokenId` from the query string and follows up with
 * `useAvailableBusinesses` + `useSelectBusinessLocation`.
 *
 * GET /v2/review-platforms/oauth/:platform/url?locationId=&returnTo=
 */
export function useOAuthAuthorizationUrl() {
  return useApiMutation<OAuthAuthorizationUrlResponse, OAuthUrlVariables>({
    mutationFn: ({ platformSlug, ...query }) =>
      apiGet<OAuthAuthorizationUrlResponse>(
        operationsClient,
        reviewPlatformsV2Endpoints.oauthAuthorizationUrl(platformSlug),
        { params: query },
      ),
  });
}

/**
 * After the OAuth callback, list the businesses / pages tied to the just-
 * completed OAuth token so the contractor can pick one.
 * GET /v2/review-platforms/oauth/:platform/available-businesses?oauthTokenId=
 */
export function useAvailableBusinesses(
  platformSlug: 'google' | 'facebook',
  oauthTokenId: string | null,
) {
  return useApiQuery<AvailableBusinessesResponse>({
    queryKey: reviewPlatformKeys.availableBusinesses(platformSlug, oauthTokenId ?? 'none'),
    queryFn: () =>
      apiGet<AvailableBusinessesResponse>(
        operationsClient,
        reviewPlatformsV2Endpoints.oauthAvailableBusinesses(platformSlug),
        { params: { oauthTokenId } },
      ),
    enabled: !!oauthTokenId,
    staleTime: 0,
  });
}
